export type CountySeoPublicationStatus = "draft" | "published";

export type CountySeoCapability =
  | "county_specific_sources"
  | "deadline_guidance"
  | "filing_guidance"
  | "condition_evidence"
  | "comparable_evidence"
  | "official_record_lookup"
  | "manual_packet_path";

export interface CountySeoSource {
  title: string;
  publisher: string;
  url: string;
  reviewedAt: string;
  official: boolean;
}

export interface CountySeoFaq {
  question: string;
  answer: string;
}

export type CountySeoEvidenceMethod =
  | "record_correction"
  | "market_value"
  | "assessment_uniformity"
  | "condition"
  | "classification"
  | "exemption"
  | "income";

export const COUNTY_SEO_EVIDENCE_METHODS = [
  "record_correction",
  "market_value",
  "assessment_uniformity",
  "condition",
  "classification",
  "exemption",
  "income",
] as const satisfies readonly CountySeoEvidenceMethod[];

export interface CountySeoDepthDetails {
  filing?: {
    form: string;
    fee: string;
    hearingRoute: string;
    laterReviewRoute: string;
    sourceUrls: string[];
    reviewedAt: string;
  };
  evidence?: {
    supportedMethods: CountySeoEvidenceMethod[];
    unsupportedMethods: CountySeoEvidenceMethod[];
    sourceUrls: string[];
    reviewedAt: string;
  };
}

export interface CountySeoPageRecord {
  status: CountySeoPublicationStatus;
  stateSlug: string;
  countySlug: string;
  countyName: string;
  stateName: string;
  stateAbbr: string;
  title: string;
  description: string;
  h1: string;
  reviewedAt: string;
  deadline: {
    display: string;
    rule: string;
    taxYear: number;
    sourceUrl: string;
    calendarRuleSourceUrl?: string;
    reviewedAt: string;
  };
  filing: {
    authorityName: string;
    portalLabel: string;
    portalUrl: string;
    methods: string[];
    sourceUrl: string;
  };
  /** Structured, source-backed depth used by operations and guide QA. */
  depthDetails?: CountySeoDepthDetails;
  capabilities: CountySeoCapability[];
  sections: {
    answer: string;
    assessmentContext: string;
    filingProcess: string;
    evidenceApproach: string;
    localConsiderations: string[];
  };
  faqs: CountySeoFaq[];
  sources: CountySeoSource[];
  /** County-specific facts used by the duplicate-content validator. */
  uniqueSignals: string[];
}

export const COUNTY_SEO_BASE_PATH = "/property-tax-appeal";
export const COUNTY_SEO_MAX_REVIEW_AGE_DAYS = 120;
export const COUNTY_SEO_MAX_DEADLINE_REVIEW_AGE_DAYS = 45;

export function countySeoPath(page: Pick<CountySeoPageRecord, "stateSlug" | "countySlug">): string {
  return `${COUNTY_SEO_BASE_PATH}/${page.stateSlug}/${page.countySlug}`;
}

export function countyIdFromSeoPage(
  page: Pick<CountySeoPageRecord, "countySlug" | "stateAbbr">,
): string {
  return `${page.countySlug.replace(/-county$/, "").replace(/-/g, "_")}_${page.stateAbbr.toLowerCase()}`;
}

export function countySeoCanonical(page: Pick<CountySeoPageRecord, "stateSlug" | "countySlug">): string {
  return `https://fairpath.app${countySeoPath(page)}`;
}

export function countySeoJurisdictionName(
  page: Pick<CountySeoPageRecord, "countyName" | "stateAbbr">,
): string {
  if (page.stateAbbr === "DC") return page.countyName;
  if (/\b(county|parish|borough|municipality|census area|city and borough)$/i.test(page.countyName)) {
    return page.countyName;
  }
  return `${page.countyName} County`;
}

export function countySeoJurisdictionLabel(
  page: Pick<CountySeoPageRecord, "countyName" | "stateAbbr" | "stateName">,
): string {
  const jurisdiction = countySeoJurisdictionName(page);
  return page.stateAbbr === "DC" ? jurisdiction : `${jurisdiction}, ${page.stateName}`;
}

function parseDate(value: string): number | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const timestamp = Date.parse(`${value}T00:00:00Z`);
  return Number.isFinite(timestamp) ? timestamp : null;
}

function ageInDays(value: string, now: Date): number | null {
  const timestamp = parseDate(value);
  if (timestamp === null) return null;
  return Math.floor((now.getTime() - timestamp) / 86_400_000);
}

function words(value: string): string[] {
  return value.toLowerCase().match(/[a-z0-9]+/g) ?? [];
}

function pageText(page: CountySeoPageRecord): string {
  return [
    page.sections.answer,
    page.sections.assessmentContext,
    page.sections.filingProcess,
    page.sections.evidenceApproach,
    ...page.sections.localConsiderations,
    ...page.faqs.flatMap((faq) => [faq.question, faq.answer]),
  ].join(" ");
}

const NATIONWIDE_PACKET_DENIAL =
  /\b(?:does not|doesn't|do not|not)\b[^.]{0,120}\b(?:paid (?:county )?packet|sell (?:a live|an automated) packet|(?:paid|active automated|automated county|automated [a-z]+) checkout|paid packet generation)\b/i;

const FUTURE_ONLY_PACKET_PATH =
  /\bFairPath\b[^.]{0,180}\b(?:for (?:a )?future cycle|future-cycle file|for future cycles)\b/i;

function shingles(value: string, size = 5): Set<string> {
  const tokens = words(value);
  const result = new Set<string>();
  for (let index = 0; index <= tokens.length - size; index += 1) {
    result.add(tokens.slice(index, index + size).join(" "));
  }
  return result;
}

function jaccard(left: Set<string>, right: Set<string>): number {
  if (left.size === 0 && right.size === 0) return 1;
  let intersection = 0;
  for (const item of left) if (right.has(item)) intersection += 1;
  return intersection / (left.size + right.size - intersection);
}

function hashString(value: string, seed: number): number {
  let hash = (2166136261 ^ seed) >>> 0;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function minHashSignature(items: Set<string>, length = 24): number[] {
  const signature = Array.from({ length }, () => 0xffffffff);
  for (const item of items) {
    for (let seed = 0; seed < length; seed += 1) {
      const hash = hashString(item, seed * 0x9e3779b1);
      if (hash < signature[seed]) signature[seed] = hash;
    }
  }
  return signature;
}

function duplicateCandidatePairs(shingleSets: readonly Set<string>[]): Set<string> {
  const buckets = new Map<string, number[]>();
  const signatures = shingleSets.map((items) => minHashSignature(items));
  const bands = 8;
  const rowsPerBand = 3;

  signatures.forEach((signature, pageIndex) => {
    for (let band = 0; band < bands; band += 1) {
      const start = band * rowsPerBand;
      const key = `${band}:${signature.slice(start, start + rowsPerBand).join(":")}`;
      const bucket = buckets.get(key) ?? [];
      bucket.push(pageIndex);
      buckets.set(key, bucket);
    }
  });

  const pairs = new Set<string>();
  for (const bucket of buckets.values()) {
    for (let left = 0; left < bucket.length; left += 1) {
      for (let right = left + 1; right < bucket.length; right += 1) {
        const first = Math.min(bucket[left], bucket[right]);
        const second = Math.max(bucket[left], bucket[right]);
        pairs.add(`${first}:${second}`);
      }
    }
  }
  return pairs;
}

export interface CountySeoValidationResult {
  path: string;
  errors: string[];
  wordCount: number;
}

export function validateCountySeoPage(
  page: CountySeoPageRecord,
  now = new Date(),
  options: { enforceFreshness?: boolean } = {},
): CountySeoValidationResult {
  const errors: string[] = [];
  const path = countySeoPath(page);
  const slugPattern = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
  const enforceFreshness = options.enforceFreshness ?? true;

  if (!slugPattern.test(page.stateSlug) || !slugPattern.test(page.countySlug)) {
    errors.push("stateSlug and countySlug must be lowercase URL-safe slugs");
  }
  if (page.title.length < 35 || page.title.length > 65) errors.push("title must be 35-65 characters");
  if (page.description.length < 120 || page.description.length > 165) {
    errors.push("description must be 120-165 characters");
  }
  if (!page.h1.includes(page.countyName) || !page.h1.includes(page.stateName)) {
    errors.push("h1 must identify the county and state");
  }

  const reviewAge = ageInDays(page.reviewedAt, now);
  if (reviewAge === null || reviewAge < 0 || (enforceFreshness && reviewAge > COUNTY_SEO_MAX_REVIEW_AGE_DAYS)) {
    errors.push(`page review must be within ${COUNTY_SEO_MAX_REVIEW_AGE_DAYS} days`);
  }
  const deadlineAge = ageInDays(page.deadline.reviewedAt, now);
  if (deadlineAge === null || deadlineAge < 0 || (enforceFreshness && deadlineAge > COUNTY_SEO_MAX_DEADLINE_REVIEW_AGE_DAYS)) {
    errors.push(`deadline review must be within ${COUNTY_SEO_MAX_DEADLINE_REVIEW_AGE_DAYS} days`);
  }
  if (!page.deadline.display || !page.deadline.rule) errors.push("deadline display and rule are required");
  const currentYear = now.getUTCFullYear();
  if (page.deadline.taxYear < currentYear - 1 || page.deadline.taxYear > currentYear + 1) {
    errors.push("deadline tax year must be current, prior-year carryover, or next-year preview");
  }

  const officialSources = page.sources.filter((source) => source.official);
  if (page.sources.length < 3 || officialSources.length < 2) {
    errors.push("at least three sources, including two official sources, are required");
  }
  const sourceUrls = new Set(page.sources.map((source) => source.url));
  const officialSourceUrls = new Set(officialSources.map((source) => source.url));
  if (sourceUrls.size !== page.sources.length) errors.push("source URLs must be unique");
  if (!sourceUrls.has(page.deadline.sourceUrl)) errors.push("deadline source must appear in sources");
  if (!sourceUrls.has(page.filing.sourceUrl)) errors.push("filing source must appear in sources");
  if (!officialSourceUrls.has(page.deadline.sourceUrl)) errors.push("deadline source must be official");
  if (page.deadline.calendarRuleSourceUrl && !officialSourceUrls.has(page.deadline.calendarRuleSourceUrl)) {
    errors.push("deadline calendar rule source must be official and appear in sources");
  }
  if (!officialSourceUrls.has(page.filing.sourceUrl)) errors.push("filing source must be official");
  for (const source of page.sources) {
    if (!source.url.startsWith("https://")) errors.push(`source must use HTTPS: ${source.url}`);
    const sourceAge = ageInDays(source.reviewedAt, now);
    if (sourceAge === null || sourceAge < 0 || (enforceFreshness && sourceAge > COUNTY_SEO_MAX_REVIEW_AGE_DAYS)) {
      errors.push(`source review is stale or invalid: ${source.url}`);
    }
  }
  if (page.depthDetails?.filing) {
    const filingDepth = page.depthDetails.filing;
    if ([filingDepth.form, filingDepth.fee, filingDepth.hearingRoute, filingDepth.laterReviewRoute].some((value) => !value.trim())) {
      errors.push("filing depth requires form, fee, hearing route, and later-review route");
    }
    validateDepthSources("filing", filingDepth.sourceUrls, sourceUrls, officialSourceUrls, errors);
    const filingDepthAge = ageInDays(filingDepth.reviewedAt, now);
    if (filingDepthAge === null || filingDepthAge < 0 || (enforceFreshness && filingDepthAge > COUNTY_SEO_MAX_REVIEW_AGE_DAYS)) {
      errors.push("filing depth review is stale or invalid");
    }
  }
  if (page.depthDetails?.evidence) {
    const evidenceDepth = page.depthDetails.evidence;
    if (evidenceDepth.supportedMethods.length === 0 || evidenceDepth.unsupportedMethods.length === 0) {
      errors.push("evidence depth must declare supported and unsupported methods");
    }
    const allMethods = [...evidenceDepth.supportedMethods, ...evidenceDepth.unsupportedMethods];
    const knownMethods = new Set<string>(COUNTY_SEO_EVIDENCE_METHODS);
    for (const method of allMethods) {
      if (!knownMethods.has(method)) errors.push(`unknown evidence method: ${method}`);
    }
    if (new Set(allMethods).size !== allMethods.length) {
      errors.push("evidence methods must be unique and cannot be both supported and unsupported");
    }
    validateDepthSources("evidence", evidenceDepth.sourceUrls, sourceUrls, officialSourceUrls, errors);
    const evidenceDepthAge = ageInDays(evidenceDepth.reviewedAt, now);
    if (evidenceDepthAge === null || evidenceDepthAge < 0 || (enforceFreshness && evidenceDepthAge > COUNTY_SEO_MAX_REVIEW_AGE_DAYS)) {
      errors.push("evidence depth review is stale or invalid");
    }
  }
  if (!page.filing.portalUrl.startsWith("https://")) errors.push("filing portal must use HTTPS");
  if (page.filing.methods.length === 0) errors.push("at least one filing method is required");

  if (page.capabilities.length < 3) errors.push("at least three capability labels are required");
  if (new Set(page.capabilities).size !== page.capabilities.length) errors.push("capability labels must be unique");
  if (page.capabilities.includes("manual_packet_path") && NATIONWIDE_PACKET_DENIAL.test(pageText(page))) {
    errors.push("manual packet pages must not deny the nationwide paid owner-guided packet");
  }
  if (page.capabilities.includes("manual_packet_path") && FUTURE_ONLY_PACKET_PATH.test(page.sections.answer)) {
    errors.push("manual packet pages must not defer the owner-guided packet to a future cycle");
  }
  if (page.sections.localConsiderations.length < 3) errors.push("at least three local considerations are required");
  if (page.faqs.length < 3) errors.push("at least three county-specific FAQs are required");
  if (page.uniqueSignals.length < 3 || page.uniqueSignals.some((signal) => words(signal).length < 6)) {
    errors.push("at least three substantive county-specific signals are required");
  }

  const sectionValues = [
    page.sections.answer,
    page.sections.assessmentContext,
    page.sections.filingProcess,
    page.sections.evidenceApproach,
  ];
  if (sectionValues.some((section) => words(section).length < 55)) {
    errors.push("each primary section must contain at least 55 words");
  }
  const wordCount = words(pageText(page)).length;
  if (wordCount < 650) errors.push("page body must contain at least 650 words");

  return { path, errors, wordCount };
}

function validateDepthSources(
  label: "filing" | "evidence",
  urls: readonly string[],
  sourceUrls: ReadonlySet<string>,
  officialSourceUrls: ReadonlySet<string>,
  errors: string[],
): void {
  if (urls.length === 0) errors.push(`${label} depth requires at least one source`);
  if (new Set(urls).size !== urls.length) errors.push(`${label} depth source URLs must be unique`);
  for (const url of urls) {
    if (!sourceUrls.has(url)) errors.push(`${label} depth source must appear in sources: ${url}`);
    if (!officialSourceUrls.has(url)) errors.push(`${label} depth source must be official: ${url}`);
  }
}

export function assertPublishableCountySeoPages(
  pages: readonly CountySeoPageRecord[],
  now = new Date(),
): CountySeoPageRecord[] {
  const published = pages.filter((page) => page.status === "published");
  const failures: string[] = [];
  const paths = new Set<string>();

  for (const page of published) {
    const result = validateCountySeoPage(page, now);
    if (paths.has(result.path)) result.errors.push("canonical path is duplicated");
    paths.add(result.path);
    if (result.errors.length > 0) failures.push(`${result.path}: ${result.errors.join("; ")}`);
  }

  const shingleSets = published.map((page) => shingles(pageText(page)));
  for (const pair of duplicateCandidatePairs(shingleSets)) {
    const [leftIndex, rightIndex] = pair.split(":").map(Number);
    const similarity = jaccard(shingleSets[leftIndex], shingleSets[rightIndex]);
    if (similarity >= 0.72) {
      failures.push(
        `${countySeoPath(published[leftIndex])} and ${countySeoPath(published[rightIndex])}: ` +
        `near-duplicate content (${Math.round(similarity * 100)}% shingle similarity)`,
      );
    }
  }

  if (failures.length > 0) {
    throw new Error(`County SEO publication checks failed:\n${failures.map((failure) => `- ${failure}`).join("\n")}`);
  }
  return published;
}

/**
 * The most recent review date anywhere on the record: page, deadline, depth
 * details, or any source. Structured data reports this as `dateModified`, so a
 * deadline re-verified today is visible to answer engines as fresh even when
 * the page body was last edited months ago.
 */
export function countySeoLatestReviewDate(page: CountySeoPageRecord): string {
  const candidates = [
    page.reviewedAt,
    page.deadline.reviewedAt,
    page.depthDetails?.filing?.reviewedAt,
    page.depthDetails?.evidence?.reviewedAt,
    ...page.sources.map((source) => source.reviewedAt),
  ].filter((value): value is string => typeof value === "string" && parseDate(value) !== null);
  const sorted = candidates.sort();
  return sorted[sorted.length - 1] ?? page.reviewedAt;
}

export function countySeoDeadlineStatus(
  page: CountySeoPageRecord,
  now = new Date(),
): "current" | "review_due" {
  const age = ageInDays(page.deadline.reviewedAt, now);
  return age !== null && age >= 0 && age <= COUNTY_SEO_MAX_DEADLINE_REVIEW_AGE_DAYS
    ? "current"
    : "review_due";
}
